import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import React, { useEffect, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { ChevronRightIcon } from '@radix-ui/react-icons';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { HashLink as Link } from 'react-router-hash-link';
import PropTypes from 'prop-types';
import { SimTypography } from 'components/SimTypography';

const NavItemResources = ({ title, id, items, colorInvert = false }) => {
	const theme = useTheme();

	const [openSubmenu, setOpenSubmenu] = useState(false);
	const [openedPopoverId, setOpenedPopoverId] = useState(null);

	const handleClick = (event, popoverId) => {
		setOpenedPopoverId(popoverId);
	};

	const [activeLink, setActiveLink] = useState('');
	useEffect(() => {
		setActiveLink(window && window.location ? window.location.pathname : '');
	}, []);

	const hasActiveLink = () => items.find(i => i.href === activeLink);
	const linkColor = colorInvert ? theme.palette.text.default : 'common.white';

	return (
		<Box>
			<DropdownMenu.Root>
				<DropdownMenu.Trigger asChild>
					<Box
						display={'flex'}
						alignItems={'center'}
						aria-describedby={id}
						sx={{ cursor: 'pointer' }}
						onClick={e => handleClick(e, id)}>
						<SimTypography
							fontWeight={openedPopoverId === id || hasActiveLink() ? 700 : 400}
							variant='menuRootText'
							color={linkColor}>
							{title}
						</SimTypography>
						<ExpandMoreIcon
							sx={{
								marginLeft: theme.spacing(1 / 4),
								width: 16,
								height: 16,
								transform: openedPopoverId === id ? 'rotate(180deg)' : 'none',
								color: linkColor
							}}
						/>
					</Box>
				</DropdownMenu.Trigger>

				<DropdownMenu.Portal>
					<DropdownMenu.Content>
						<Box
							bgcolor={theme.palette.background.default}
							paddingX={2}
							paddingTop={4}
							paddingBottom={1}
							borderRadius={2}
							zIndex={3}>
							<DropdownMenu.Item>
								{items.map((p, idx) => (
									<Box key={p.body.href || idx}>
										{p.body.map(bodyItem => (
											<DropdownMenu.Sub key={bodyItem.href}>
												<DropdownMenu.SubTrigger>
													<Box display='flex' alignItems='center'>
														<Button
															height={'40px'}
															component={'a'}
															href={p.body.href}
															fullWidth
															sx={{
																justifyContent: 'flex-start',
																paddingLeft: bodyItem.isTabLink === 1 ? 2 : 0,
																color:
																	activeLink === bodyItem.href
																		? theme.palette.primary.main
																		: theme.palette.text.primary,
																backgroundColor:
																	activeLink === bodyItem.href
																		? alpha(theme.palette.primary.main, 0.1)
																		: 'transparent',
																fontWeight: activeLink === bodyItem.href ? 600 : 400
															}}>
															<Link
																to={bodyItem.href}
																activeStyle={{ textDecoration: 'none' }}>
																<Box paddingLeft={2}>
																	<SimTypography
																		variant='menuRootText'
																		lineHeight={'normal'}
																		color={theme.palette.text.secondary}
																		sx={{
																			textDecoration: 'underline',
																			textDecorationColor: alpha(
																				theme.palette.background.default,
																				1
																			)
																		}}>
																		{bodyItem.title}
																	</SimTypography>
																</Box>
															</Link>
														</Button>
														{bodyItem.sections && <ChevronRightIcon />}
													</Box>
												</DropdownMenu.SubTrigger>

												<DropdownMenu.Portal>
													{openSubmenu && (
														<DropdownMenu.SubContent
															onMouseEnter={() => {
																setOpenSubmenu(true);
															}}
															onMouseLeave={() => {
																setOpenSubmenu(false);
															}}>
															<Box
																bgcolor={theme.palette.background.default}
																paddingX={3}
																zIndex={3}
																borderRadius={2}>
																<DropdownMenu.Item>
																	{bodyItem.sections.map(z => (
																		<Box>
																			<Button
																				height={'40px'}
																				margin={0}
																				padding={0}
																				component={'a'}
																				fullWidth
																				sx={{
																					justifyContent: 'flex-start',
																					color:
																						activeLink ===
																						z.sectionAnchorLink
																							? theme.palette.primary.main
																							: theme.palette.text
																									.primary,
																					backgroundColor:
																						activeLink ===
																						z.sectionAnchorLink
																							? alpha(
																									theme.palette
																										.primary.main,
																									0.1
																							  )
																							: 'transparent',
																					fontWeight:
																						activeLink ===
																						z.sectionAnchorLink
																							? 600
																							: 400
																				}}>
																				<Link to={z.sectionAnchorLink}>
																					<SimTypography
																						variant='menuRootText'
																						color={
																							theme.palette.text.secondary
																						}
																						sx={{
																							textDecoration: 'underline',
																							textDecorationColor: alpha(
																								theme.palette.background
																									.paper,
																								1
																							)
																						}}>
																						{z.sectionTitle}
																					</SimTypography>
																				</Link>
																			</Button>
																		</Box>
																	))}
																</DropdownMenu.Item>
															</Box>
														</DropdownMenu.SubContent>
													)}
												</DropdownMenu.Portal>
											</DropdownMenu.Sub>
										))}
									</Box>
								))}
							</DropdownMenu.Item>
						</Box>
					</DropdownMenu.Content>
				</DropdownMenu.Portal>
			</DropdownMenu.Root>
		</Box>
	);
};

NavItemResources.propTypes = {
	title: PropTypes.string.isRequired,
	id: PropTypes.string.isRequired,
	items: PropTypes.array.isRequired,
	colorInvert: PropTypes.bool
};

export default NavItemResources;
